import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StepHandle, StepProps } from '../BusinessProfileSetupPage';
import { businessProfileSetupGateway } from '../../services/BusinessProfileSetupGateway';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ApiError } from '../../../identity/types';
import type { WorkLocationResponse } from '../../types';
import { RestrictionLockedDialog } from '../../../../shared/LockedFeatureDialog';
import {
  clearSubscriptionRestrictionNotice, restrictionMessage, upgradeRequiredFromError, type SubscriptionRestriction,
} from '../../../../shared/subscriptionRestriction';
import { LocationCard } from '../../work-locations/LocationCard';
import { LocationEditor } from '../../work-locations/LocationEditor';
import { LocationFilterBar } from '../../work-locations/LocationFilterBar';
import { applyFilters, emptyFilters, hasActiveFilters, type LocationFilters } from '../../work-locations/locationFilters';

type PendingAction = { kind: 'archive'; location: WorkLocationResponse } | { kind: 'reactivate'; location: WorkLocationResponse };
type EditorState = { existing: WorkLocationResponse | null; tab: 'branch' | 'registration' } | null;

/**
 * Step 5, Branches / Work Locations (Figma S01-S32). The list (Active and Inactive groups, filters, search, cards with their registration
 * warning) and the Add / Edit editor are separate components; this one owns the data, the filters, and the archive / reactivate flows.
 *
 * Archive is the only way a location leaves use (never a delete): it is confirmed first, the server then checks the Employees module at that
 * moment, and only a server-confirmed result is shown. If employees are still assigned the "cannot be archived" card links to All Employees
 * filtered to that branch. Nothing here files, verifies or finalizes anything: a location whose registration is incomplete is shown with a warning
 * and is never reported as ready.
 */
export const WorkLocationsStep = forwardRef<StepHandle, StepProps>(function WorkLocationsStep({ markStepComplete, setFooterHidden, readOnly, onEditMainLocation }, ref) {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<WorkLocationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(null);
  const [filters, setFilters] = useState<LocationFilters>(emptyFilters());
  const [notice, setNotice] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [blocked, setBlocked] = useState<WorkLocationResponse | null>(null);
  // Reactivating a location in a second state is refused when the plan covers one state (SUBSCRIPTION_UPGRADE_REQUIRED). The location stays
  // exactly as it was (inactive, with all its details); the explanation says why and where to compare plans.
  const [planRefusal, setPlanRefusal] = useState<SubscriptionRestriction | null>(null);
  const editorDirty = useRef(false);

  const load = useCallback(async () => {
    const list = await businessProfileSetupGateway.listWorkLocations();
    setLocations(list);
    return list;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch {
        setLoadError('Could not load your work locations. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const showEditor = editor !== null;
  useEffect(() => {
    setFooterHidden?.(showEditor);
    return () => setFooterHidden?.(false);
  }, [showEditor, setFooterHidden]);

  const onDirtyChange = useCallback((dirty: boolean) => {
    editorDirty.current = dirty;
  }, []);

  const backToList = async (message?: string) => {
    setEditor(null);
    editorDirty.current = false;
    setApiError(null);
    try {
      await load();
    } catch {
      setApiError('Could not refresh your work locations. Please refresh the page.');
    }
    setNotice(message ?? null);
  };

  const reloadEditor = async (id: string) => {
    try {
      const list = await load();
      const latest = list.find((l) => l.id === id) ?? null;
      setEditor(latest ? { existing: latest, tab: 'branch' } : null);
    } catch {
      setApiError('Could not reload this location. Please refresh the page.');
      setEditor(null);
    }
  };

  const openAdd = () => {
    setNotice(null);
    setApiError(null);
    setEditor({ existing: null, tab: 'branch' });
  };

  const openEdit = (location: WorkLocationResponse, tab: 'branch' | 'registration' = 'branch') => {
    setNotice(null);
    setApiError(null);
    setEditor({ existing: location, tab });
  };

  const archive = async (location: WorkLocationResponse) => {
    setPendingAction(null);
    setApiError(null);
    setNotice(null);
    try {
      await businessProfileSetupGateway.archiveWorkLocation(location.id);
      await load();
      setNotice(`"${location.name}" was archived. Its history stays available for records and payroll reports, and you can reactivate it later.`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'WORK_LOCATION_HAS_ASSIGNED_EMPLOYEES') {
        setBlocked(location);
      } else {
        setApiError(err instanceof ApiError ? err.message : 'Could not archive this location. Nothing was changed.');
      }
    }
  };

  const reactivate = async (location: WorkLocationResponse) => {
    setPendingAction(null);
    setApiError(null);
    setNotice(null);
    try {
      await businessProfileSetupGateway.reactivateWorkLocation(location.id);
      await load();
      setNotice(`"${location.name}" was reactivated.`);
    } catch (err) {
      const upgrade = upgradeRequiredFromError(err);
      if (upgrade) {
        clearSubscriptionRestrictionNotice();
        setApiError(`${restrictionMessage(upgrade)} "${location.name}" stays not active for payroll, with all its details kept.`);
        setPlanRefusal(upgrade);
        return;
      }
      setApiError(err instanceof ApiError ? err.message : 'Could not reactivate this location.');
    }
  };

  useImperativeHandle(ref, () => ({
    async saveAndContinue() {
      if (!locations.some((l) => l.status === 'Active' && l.isDefault)) {
        setApiError('A Main location is required before continuing.');
        throw new Error('no default location');
      }
      markStepComplete('work-locations');
    },
    async saveAndExit() {
      // Every location is saved the moment it is added, edited, archived or reactivated, so there is nothing further to persist here. (The
      // shared wizard footer is hidden while the editor is open, so an unsaved editor is never reached from it.)
    },
    isDirty() {
      // Only an open editor holding real, unsaved changes counts: a freshly opened blank Add form is not yet "unsaved changes".
      return showEditor && editorDirty.current;
    },
    async commitEdits() {
      // Nothing to batch-commit: every action above already saved through its own server-confirmed call.
    },
  }));

  const filtered = useMemo(() => applyFilters(locations, filters), [locations, filters]);
  const active = filtered.filter((l) => l.status === 'Active');
  const inactive = filtered.filter((l) => l.status !== 'Active');

  if (loading) return <p className="helper">Loading…</p>;

  if (editor) {
    return (
      <LocationEditor
        key={editor.existing?.id ?? 'new'}
        existing={editor.existing}
        initialTab={editor.tab}
        onBackToList={(message) => void backToList(message)}
        onReload={(id) => void reloadEditor(id)}
        onEditMainAddress={editor.existing?.isDefault ? onEditMainLocation : undefined}
        onDirtyChange={onDirtyChange}
      />
    );
  }

  const noMatches = filtered.length === 0;
  const filtering = hasActiveFilters(filters);

  return (
    <div className="loc-list">
      <LocationFilterBar filters={filters} onChange={setFilters} onAdd={readOnly ? undefined : openAdd} />

      {loadError && <div className="notice error-note" role="alert">{loadError}</div>}
      {notice && <div className="notice success-note" role="status">{notice}</div>}
      {apiError && <div className="notice error-note" role="alert">{apiError}</div>}

      {noMatches ? (
        <div className="notice empty-state" role="status">
          {locations.length === 0
            ? 'No work locations yet. Your main location is created from your Company Address in Step 2.'
            : filtering
              ? 'No locations match your search or filters.'
              : 'No locations to show.'}
          {filtering && locations.length > 0 && (
            <button type="button" className="link-btn" onClick={() => setFilters(emptyFilters())}>Clear All</button>
          )}
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <>
              <div className="section-title-row"><span className="title">Active Branches ({active.length})</span></div>
              <div className="cards">
                {active.map((l) => (
                  <LocationCard key={l.id} location={l} readOnly={readOnly} onEdit={openEdit} onArchive={(x) => setPendingAction({ kind: 'archive', location: x })} onReactivate={(x) => setPendingAction({ kind: 'reactivate', location: x })} />
                ))}
              </div>
            </>
          )}
          {inactive.length > 0 && (
            <>
              <div className="section-title-row"><span className="title">Inactive Branches ({inactive.length})</span></div>
              <div className="cards">
                {inactive.map((l) => (
                  <LocationCard key={l.id} location={l} readOnly={readOnly} onEdit={openEdit} onArchive={(x) => setPendingAction({ kind: 'archive', location: x })} onReactivate={(x) => setPendingAction({ kind: 'reactivate', location: x })} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      <ConfirmDialog
        open={pendingAction?.kind === 'archive'}
        title="Archive this branch?"
        // Follows the approved SRS's "Archive Branch Confirmation Prompt": unavailable for new assignments, history kept.
        body={pendingAction ? `"${pendingAction.location.name}" will no longer be available for new employee assignments. Its history stays available for records and payroll reports, and you can reactivate it later.` : ''}
        confirmLabel="Archive Branch"
        destructive
        onCancel={() => setPendingAction(null)}
        onConfirm={() => { if (pendingAction) void archive(pendingAction.location); }}
      />
      <ConfirmDialog
        open={pendingAction?.kind === 'reactivate'}
        title="Reactivate this location?"
        body={pendingAction ? `"${pendingAction.location.name}" will be marked active again and can be assigned to employees.` : ''}
        confirmLabel="Reactivate"
        onCancel={() => setPendingAction(null)}
        onConfirm={() => { if (pendingAction) void reactivate(pendingAction.location); }}
      />
      {planRefusal && (
        <RestrictionLockedDialog restriction={planRefusal} fallbackFeatureName="Multi-state payroll" onClose={() => setPlanRefusal(null)} />
      )}
      <ConfirmDialog
        open={blocked !== null}
        title="Branch Cannot be Archived."
        body="There are currently active employees assigned to it. You must reassign all employees to another branch before this one can be archived."
        confirmLabel="Go to Employees List"
        onCancel={() => setBlocked(null)}
        onConfirm={() => { if (blocked) navigate(`/people/employees?branch=${encodeURIComponent(blocked.id)}`); }}
      />
    </div>
  );
});
